import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Send, Loader, ChevronDown, Check, Target, GraduationCap, Cloud, Code, Brain, Bot, Sparkles, Eye, Mic, TrendingUp, Database, Lock, Scale, Blocks } from 'lucide-react';
import { SecureApiService, FormData } from '../services/SecureApiService';
import { FormValidation } from '../types';
import { createInitialValidation, validateField } from '../utils/validation';
import { Modal } from './Modal';

interface ContactFormProps {
  apiService: SecureApiService;
}

export const ContactForm: React.FC<ContactFormProps> = ({ apiService }) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    service: "",
    message: "",
    recaptchaToken: "",
  });
  const [formValidation, setFormValidation] = useState<FormValidation>(
    createInitialValidation()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({ show: false, title: "", message: "" });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const serviceOptions = [
    {
      category: "Services", 
      options: [
        { value: "reach", label: "Cognitive Reach", subtitle: "Digital Marketing", icon: <Target className="w-4 h-4" /> },
        { value: "train", label: "Cognitive Train", subtitle: "AI Training & Consulting", icon: <GraduationCap className="w-4 h-4" /> },
        { value: "cloud", label: "Cognitive Cloud", subtitle: "Infrastructure", icon: <Cloud className="w-4 h-4" /> },
        { value: "build", label: "Cognitive Build", subtitle: "Custom Software", icon: <Code className="w-4 h-4" /> },
        { value: "intelligence", label: "Cognitive Intelligence", subtitle: "AI & Data", icon: <Brain className="w-4 h-4" /> },
        { value: "agents", label: "Cognitive Agents", subtitle: "AI Automation & RPA", icon: <Bot className="w-4 h-4" /> },
        { value: "generate", label: "Cognitive Generate", subtitle: "Generative AI", icon: <Sparkles className="w-4 h-4" /> },
        { value: "vision", label: "Cognitive Vision", subtitle: "Computer Vision", icon: <Eye className="w-4 h-4" /> },
        { value: "voice", label: "Cognitive Voice", subtitle: "Conversational AI", icon: <Mic className="w-4 h-4" /> },
        { value: "predict", label: "Cognitive Predict", subtitle: "Advanced Analytics", icon: <TrendingUp className="w-4 h-4" /> },
        { value: "iot", label: "Cognitive IoT", subtitle: "Internet of Things", icon: <Database className="w-4 h-4" /> },
        { value: "secure", label: "Cognitive Secure", subtitle: "Cybersecurity", icon: <Lock className="w-4 h-4" /> },
        { value: "compliance", label: "Cognitive Compliance", subtitle: "Regulatory AI", icon: <Scale className="w-4 h-4" /> },
        { value: "ledger", label: "Cognitive Ledger", subtitle: "Blockchain Technology", icon: <Blocks className="w-4 h-4" /> }
      ]
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getSelectedOption = () => {
    for (const category of serviceOptions) {
      const option = category.options.find(opt => opt.value === formData.service);
      if (option) return option;
    }
    return null;
  };

  const handleServiceSelect = (value: string) => {
    setFormData(prev => ({ ...prev, service: value }));
    setIsDropdownOpen(false);
    const validation = validateField('service', value);
    setFormValidation(prev => ({ ...prev, service: validation }));
  };
  
  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleInputBlur = useCallback(
    (
      e: React.FocusEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      const validation = validateField(name as keyof FormData, value);
      setFormValidation((prev) => ({ ...prev, [name]: validation }));
    },
    []
  );
  
  const validateForm = (): boolean => {
    let isValid = true;
    const newValidation = { ...formValidation };

    Object.keys(formData).forEach((key) => {
      if (key !== 'recaptchaToken') {
        const validation = validateField(key as keyof FormData, formData[key as keyof FormData]);
        newValidation[key as keyof FormValidation] = validation;
        if (!validation.isValid) {
          isValid = false;
        }
      }
    });
    setFormValidation(newValidation);
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (isSubmitting) return;

    if (!validateForm()) {
      setModal({
        show: true,
        title: "Validation Error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await apiService.submitContactForm(formData);

      if (result.success) {
        setModal({
          show: true,
          title: "Message Sent Successfully!",
          message:
            result.message ||
            "Thank you for your message! We will get back to you soon.",
        });
        
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          company: "",
          service: "",
          message: "",
          recaptchaToken: "",
        });
        setFormValidation(createInitialValidation());
      } else {
        setModal({
          show: true,
          title: "Submission Failed",
          message:
            result.errors?.join(", ") ||
            "There was an error sending your message. Please try again.",
        });
      }
    } catch (error) {
      setModal({
        show: true,
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Get Started Today
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                First Name <span className="text-blue-400">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all"
                placeholder="John"
                required
              />
              {formValidation.firstName.touched &&
                formValidation.firstName.error && (
                  <p className="mt-1 text-sm text-blue-400">
                    {formValidation.firstName.error}
                  </p>
                )}
            </div>
            
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Last Name <span className="text-blue-400">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all"
                placeholder="Doe"
                required
              />
              {formValidation.lastName.touched &&
                formValidation.lastName.error && (
                  <p className="mt-1 text-sm text-blue-400">
                    {formValidation.lastName.error}
                  </p>
                )}
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Email <span className="text-blue-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all"
              placeholder="john@company.com"
              required
            />
            {formValidation.email.touched && formValidation.email.error && (
              <p className="mt-1 text-sm text-blue-400">
                {formValidation.email.error}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="company"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all"
              placeholder="Your Company"
            />
            {formValidation.company.touched && formValidation.company.error && (
              <p className="mt-1 text-sm text-blue-400">
                {formValidation.company.error}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="service"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Service Interest <span className="text-blue-400">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              {/* Custom Dropdown Button */}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full px-4 py-4 sm:py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-left text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all hover:border-blue-300 dark:hover:border-blue-500 min-h-[48px] sm:min-h-[auto]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getSelectedOption() && (
                      <div className="text-blue-600 dark:text-blue-400">
                        {getSelectedOption()?.icon}
                      </div>
                    )}
                    <div>
                      {getSelectedOption() ? (
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {getSelectedOption()?.label}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {getSelectedOption()?.subtitle}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">Select a service</span>
                      )}
                    </div>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`} 
                  />
                </div>
              </button>

              {/* Custom Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl dark:shadow-2xl max-h-80 overflow-y-auto">
                  {serviceOptions.map((category) => (
                    <div key={category.category} className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {category.category}
                      </div>
                      {category.options.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleServiceSelect(option.value)}
                          className={`w-full flex items-center space-x-3 px-3 py-3 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                            formData.service === option.value 
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' 
                              : ''
                          }`}
                        >
                          <div className="text-blue-600 dark:text-blue-400">
                            {option.icon}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {option.label}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {option.subtitle}
                            </div>
                          </div>
                          {formData.service === option.value && (
                            <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Hidden input for form validation */}
              <input
                type="hidden"
                name="service"
                value={formData.service}
                required
              />
            </div>
            {formValidation.service.touched && formValidation.service.error && (
              <p className="mt-1 text-sm text-blue-400">
                {formValidation.service.error}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Message <span className="text-blue-400">*</span>
            </label>
            <textarea
              rows={4}
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 transition-all resize-none"
              placeholder="Tell us about your project, service needs, timeline, and any specific requirements..."
              required
            />
            {formValidation.message.touched && formValidation.message.error && (
              <p className="mt-1 text-sm text-blue-400">
                {formValidation.message.error}
              </p>
            )}
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            By submitting this form, you consent to us contacting you regarding your inquiry. 
            We respect your privacy and will not share your information with third parties. 
            See our <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">Privacy Policy</a> for details.
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full px-6 py-4 font-semibold rounded-lg focus:outline-none focus:ring-4 transition-all duration-300 ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl transform hover:scale-105"
            } text-white`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 mr-2 inline animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send Message
                <Send className="w-5 h-5 ml-2 inline" />
              </>
            )}
          </button>
        </form>
      </section>

      <Modal
        isOpen={modal.show}
        onClose={() => setModal({ show: false, title: "", message: "" })}
        title={modal.title}
        message={modal.message}
      />
    </>
  );
};